import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

interface ErrorToastProps {
  duration?: number
}

const errorMessages: Record<string, string> = {
  'platform-not-configured': 'This platform link is not yet configured. Please check back later.',
  'unknown': 'An unexpected error occurred. Please try again.',
}

export function ErrorToast({ duration = 5000 }: ErrorToastProps) {
  const [searchParams, setSearchParams] = useSearchParams()
  const [isVisible, setIsVisible] = useState(false)
  const [message, setMessage] = useState('')

  useEffect(() => {
    const errorCode = searchParams.get('error')

    if (errorCode) {
      setMessage(errorMessages[errorCode] || errorMessages['unknown'])
      setIsVisible(true)

      // Clear the error param from URL
      const newParams = new URLSearchParams(searchParams)
      newParams.delete('error')
      setSearchParams(newParams, { replace: true })

      // Auto-hide after duration
      const timer = setTimeout(() => {
        setIsVisible(false)
      }, duration)

      return () => clearTimeout(timer)
    }
  }, [searchParams, setSearchParams, duration])

  const handleDismiss = () => {
    setIsVisible(false)
  }

  if (!isVisible) {
    return null
  }

  return (
    <div
      data-testid="error-toast"
      role="alert"
      aria-live="polite"
      className={`
        fixed bottom-6 left-1/2 -translate-x-1/2 z-50
        max-w-md w-full mx-6
        px-6 py-4 rounded-lg shadow-xl
        bg-red-900/90 text-white backdrop-blur-sm
        transition-all duration-500 ease-cinematic
        ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}
      `}
    >
      <div className="flex items-start gap-3">
        {/* Error icon */}
        <svg
          className="w-5 h-5 mt-0.5 flex-shrink-0"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>

        {/* Message */}
        <p className="flex-1 text-sm">{message}</p>

        {/* Dismiss button */}
        <button
          onClick={handleDismiss}
          className="p-1 hover:bg-white/10 rounded transition-colors duration-200"
          aria-label="Dismiss"
        >
          <svg
            className="w-4 h-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
    </div>
  )
}
